import "styles/tabSpecInspector.css"
import React, { useMemo, useRef, useState } from "react"
import { Button, Drawer, Modal, Tabs, Tooltip } from "antd"
import type { QueryQueue } from "features/Dashboard/runtime/queryQueue"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { showsSiblings } from "../../compiler/table"
import { describeFinding, findingsForTable } from "../../model/reconcile"
import { ShownNames, shownNames } from "../../model/shownLabels"
import { sliceWords } from "../../model/summaries"
import { selectPlan, useTabSpecStore } from "../../runtime/tabSpecStore"
import { Catalogue, findInstance, findQuestion } from "../../types/catalogue"
import type { TableSpec } from "../../types/tabSpecDocument"
import { OVERRIDE_FIELDS } from "../Grid/describe"
import { AI_TAG_TITLE } from "../Grid/gridColumns"
import BaseTab from "./BaseTab.component"
import ColumnsTab from "./ColumnsTab.component"
import type { DraftGroup } from "./ConditionGroups.component"
import NotesTab from "./NotesTab.component"
import Preview from "./Preview.component"
import RowsTab from "./RowsTab.component"
import StatisticsTab from "./StatisticsTab.component"

export interface InspectorProps {
  /** The session's query queue; the preview runs the table through it. */
  queue: QueryQueue | null
  /** Opens the banner editor. Without it the Columns tab has no "Edit banners". */
  onEditBanners?: () => void
}

/**
 * The table's question, with its grid row or loop, or "summary" for a grid
 * summary, as the tables show them (`names`).
 */
const tableName = (table: TableSpec, catalogue: Catalogue | null, names: ShownNames): string => {
  const variable = names.variable(table.question)
  const question = catalogue ? findQuestion(catalogue, table.question, table.source) : null
  // A grid summary fixed at some values names them.
  if (table.kind === "grid-summary")
    return question && table.slice
      ? `${variable} - ${sliceWords(question, table.slice, names)} summary`
      : `${variable} summary`
  if (!table.instance) return variable
  const instance = question ? findInstance(question, table.instance) : null
  return `${variable} - ${names.instance(table.question, table.instance, instance?.label || table.instance)}`
}

/** Settings on the left, the table on the right, what to review above them. */
const InspectorBody: React.FC<{
  table: TableSpec
  queue: QueryQueue | null
  onEditBanners?: () => void
  tab: string
  onTab: (tab: string) => void
}> = ({ table, queue, onEditBanners, tab, onTab }) => {
  const document = useTabSpecStore((s) => s.document)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const findings = useTabSpecStore((s) => s.findings)
  // What the settings give: the findings and the plan's warnings. What a run
  // found (the check of a table from the data) is said once, by the preview
  // under the table. A grid's rows laid out one row each that cannot be made
  // (no row chosen, or a row the table does not have) says why at once,
  // before any run.
  const problems = useMemo(() => {
    const plan = selectPlan({ document, catalogue }, table.id)
    const names = shownNames(document.labels)
    const q = catalogue ? findQuestion(catalogue, table.question, table.source) : null
    const rowLayout =
      q !== null && showsSiblings(table, q) && table.siblingLayout === "row"
    return [
      ...findingsForTable(findings, table.id).map((f) => describeFinding(f, names)),
      ...(plan?.warnings ?? []),
      ...(rowLayout && plan?.status === "skip" && plan.skipReason ? [plan.skipReason] : []),
    ]
  }, [table, document, catalogue, findings])

  // The filter's groups as last shown, a group added and not yet filled
  // included: the Base tab is not kept while another tab is shown. The body
  // is keyed by table, so this is the table's alone.
  const filterDraft = useRef<DraftGroup[] | null>(null)

  const id = table.id
  const settings =
    table.kind === "text" ? (
      <div className="ts-tab">
        <p className="ts-hint">Text answers are not tabulated.</p>
        <NotesTab tableId={id} />
      </div>
    ) : (
      <Tabs
        size="small"
        className="ts-inspector__tabs"
        activeKey={tab}
        onChange={onTab}
        // A tab that is not shown is not kept: it would render on every edit.
        destroyOnHidden
        items={[
          { key: "rows", label: "Rows", children: <RowsTab tableId={id} /> },
          {
            key: "columns",
            label: "Columns",
            children: <ColumnsTab tableId={id} onEditBanners={onEditBanners} />,
          },
          {
            key: "base",
            label: "Base and filter",
            children: <BaseTab tableId={id} filterDraft={filterDraft} />,
          },
          { key: "statistics", label: "Statistics", children: <StatisticsTab tableId={id} /> },
          { key: "notes", label: "Notes", children: <NotesTab tableId={id} /> },
        ]}
      />
    )

  return (
    <div className="ts-inspector">
      {problems.length > 0 && (
        <ul className="ts-inspector__problems" aria-label="Things to review">
          {problems.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      )}
      <div className="ts-inspector__columns">
        <div className="ts-inspector__settings">{settings}</div>
        <div className="ts-inspector__preview">
          <Preview tableId={id} queue={queue} />
        </div>
      </div>
    </div>
  )
}

/**
 * The drawer of the selected table: its settings and a live preview of the
 * table. It opens while a table is selected; closing it clears the
 * selection. The footer steps through the tables in the spec's order and
 * puts the table back to the defaults, without its filter. The open tab
 * stays put from one table to the next; the contents are keyed by table,
 * so nothing typed carries over.
 */
const Inspector: React.FC<InspectorProps> = ({ queue, onEditBanners }) => {
  const tables = useTabSpecStore((s) => s.document.tables)
  const selectedId = useTabSpecStore((s) => s.selectedTableId)
  const aiTableIds = useTabSpecStore((s) => s.aiTableIds)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const labels = useTabSpecStore((s) => s.document.labels)
  const names = useMemo(() => shownNames(labels), [labels])
  const select = useTabSpecStore((s) => s.select)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const [tab, setTab] = useState("rows")
  /** The table waiting for the reset to be confirmed. */
  const [resetting, setResetting] = useState<string | null>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  // The drawer renders inside the spec, so it gets the --qc-* tokens.
  const container = (): HTMLElement =>
    anchorRef.current?.closest<HTMLElement>(".tabspec-root") ?? window.document.body

  const index = tables.findIndex((t) => t.id === selectedId)
  const table = index >= 0 ? tables[index] : null
  const number = table?.include
    ? tables.slice(0, index + 1).filter((t) => t.include).length
    : null
  const title = !table
    ? ""
    : number !== null
      ? `Table ${number}: ${tableName(table, catalogue, names)}`
      : `Excluded table: ${tableName(table, catalogue, names)}`
  const ai = table !== null && aiTableIds.includes(table.id)

  const confirmReset = (): void => {
    if (resetting) {
      clearOverrides(resetting, OVERRIDE_FIELDS)
      // The filter is set on the table alone; the type ("Show as") stays.
      updateTable(resetting, { filter: [] })
    }
    setResetting(null)
  }

  const footer = table && (
    <div className="ts-inspector__footer">
      <Button size="small" disabled={index <= 0} onClick={() => select(tables[index - 1].id)}>
        Previous table
      </Button>
      <Button
        size="small"
        disabled={index >= tables.length - 1}
        onClick={() => select(tables[index + 1].id)}
      >
        Next table
      </Button>
      <Button
        size="small"
        className="ts-inspector__reset"
        onClick={() => setResetting(table.id)}
      >
        Reset table to defaults
      </Button>
    </div>
  )

  return (
    <>
      <span ref={anchorRef} hidden />
      <Drawer
        open={table !== null}
        onClose={() => select(null)}
        getContainer={container}
        rootClassName="ts-drawer"
        classNames={{ body: "ts-inspector__drawer-body" }}
        placement="right"
        width="min(1280px, 94vw)"
        title={
          ai ? (
            <span className="ts-inspector__title">
              <span>{title}</span>
              <Tooltip title={AI_TAG_TITLE}>
                <span className="ts-chip" data-tone="good" aria-label={AI_TAG_TITLE}>
                  AI
                </span>
              </Tooltip>
            </span>
          ) : (
            title
          )
        }
        footer={footer}
        destroyOnHidden
      >
        {table && (
          <InspectorBody
            key={table.id}
            table={table}
            queue={queue}
            onEditBanners={onEditBanners}
            tab={tab}
            onTab={setTab}
          />
        )}
      </Drawer>
      {/* A confirmation in the platform's words and buttons: the way back
          first, the destructive action last. */}
      <Modal
        open={resetting !== null}
        title="Reset table to defaults"
        onCancel={() => setResetting(null)}
        getContainer={container}
        destroyOnHidden
        footer={[
          <DataQButton key="cancel" type="secondary" onClick={() => setResetting(null)}>
            Cancel
          </DataQButton>,
          <DataQButton key="reset" type="danger" onClick={confirmReset}>
            Reset
          </DataQButton>,
        ]}
      >
        <p>
          Everything set on this table goes back to the spec&apos;s defaults, and its filter is
          removed. The table keeps its type.
        </p>
      </Modal>
    </>
  )
}

export default Inspector
